<template>
  <div>
    <nav>
      <div class="logo-nav">
        <img alt="Agritest Logo" src="@/assets/images/logo.png" />
      </div>
    </nav>

    <div class="interface">
      <img class="image" src="@/assets/images/fundo.jpg" alt="Fundo" />
      <div class="image-overlay"></div>
      <b-card class="reset-card">
        <div class="reset-card-content">
          <!-- Error State - Invalid/Expired Token -->
          <div v-if="tokenError" class="error-state">
            <div class="error-icon">
              <i class="fas fa-exclamation-triangle"></i>
            </div>
            <h2 class="title error-title">Link Inválido</h2>
            <p class="message">
              Este link de redefinição de senha é inválido ou expirou.
              Por favor, solicite um novo link.
            </p>
            <router-link to="/forgot-password" class="btn-request-new">
              <i class="fas fa-redo"></i>
              <span>Solicitar Novo Link</span>
            </router-link>
            <div class="back-link">
              <router-link to="/login">
                <i class="fas fa-arrow-left"></i>
                Voltar ao Login
              </router-link>
            </div>
          </div>

          <!-- Success State -->
          <div v-else-if="resetSuccess" class="success-state">
            <div class="success-icon">
              <i class="fas fa-check-circle"></i>
            </div>
            <h2 class="title">Senha Redefinida!</h2>
            <p class="message">
              Sua senha foi alterada com sucesso.
              Agora você pode fazer login com sua nova senha.
            </p>
            <router-link to="/login" class="btn-login">
              <span>Fazer Login</span>
              <i class="fas fa-arrow-right"></i>
            </router-link>
          </div>

          <!-- Form State -->
          <b-form v-else @submit.prevent="submitResetPassword">
            <div class="reset-header">
              <div class="header-icon">
                <i class="fas fa-key"></i>
              </div>
              <h2 class="title">Redefinir Senha</h2>
              <p class="subtitle">
                Digite sua nova senha abaixo.
              </p>
            </div>

            <div class="form-group">
              <label class="label">
                <i class="fas fa-lock label-icon"></i>
                Nova Senha
              </label>
              <div class="password-wrapper">
                <b-form-input
                  v-model="newPassword"
                  :type="showPassword ? 'text' : 'password'"
                  placeholder="Digite sua nova senha"
                  autocomplete="new-password"
                  name="password"
                  required
                  class="modern-input"
                  :disabled="loading"
                  :state="passwordValidation"
                ></b-form-input>
                <button
                  type="button"
                  class="password-toggle"
                  @click="showPassword = !showPassword"
                  :aria-label="showPassword ? 'Ocultar senha' : 'Mostrar senha'"
                >
                  <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                </button>
              </div>
              <small class="password-hint">
                A senha deve ter pelo menos 6 caracteres
              </small>
            </div>

            <div class="form-group">
              <label class="label">
                <i class="fas fa-lock label-icon"></i>
                Confirmar Senha
              </label>
              <div class="password-wrapper">
                <b-form-input
                  v-model="confirmPassword"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  placeholder="Confirme sua nova senha"
                  autocomplete="new-password"
                  name="confirm-password"
                  required
                  class="modern-input"
                  :disabled="loading"
                  :state="confirmPasswordValidation"
                ></b-form-input>
                <button
                  type="button"
                  class="password-toggle"
                  @click="showConfirmPassword = !showConfirmPassword"
                  :aria-label="showConfirmPassword ? 'Ocultar senha' : 'Mostrar senha'"
                >
                  <i :class="showConfirmPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                </button>
              </div>
              <small v-if="confirmPassword && !confirmPasswordValidation" class="password-error">
                As senhas não coincidem
              </small>
            </div>

            <b-button
              class="btn-submit"
              type="submit"
              :disabled="loading || !isFormValid"
            >
              <b-spinner v-if="loading" small class="mr-2"></b-spinner>
              <span v-if="loading">Redefinindo...</span>
              <template v-else>
                <span>Redefinir Senha</span>
                <i class="fas fa-check"></i>
              </template>
            </b-button>

            <div class="back-link">
              <router-link to="/login">
                <i class="fas fa-arrow-left"></i>
                Voltar ao Login
              </router-link>
            </div>
          </b-form>
        </div>
      </b-card>
    </div>
  </div>
</template>

<script>
import { request } from '@/services'
import { toast } from '@/utils'
import { DB_ROUTES } from '../constants'

export default {
  name: 'ResetPassword',
  data: () => ({
    token: '',
    newPassword: '',
    confirmPassword: '',
    showPassword: false,
    showConfirmPassword: false,
    loading: false,
    resetSuccess: false,
    tokenError: false
  }),
  computed: {
    passwordValidation () {
      if (!this.newPassword) return null
      return this.newPassword.length >= 6
    },
    confirmPasswordValidation () {
      if (!this.confirmPassword) return null
      return this.confirmPassword === this.newPassword && this.newPassword.length >= 6
    },
    isFormValid () {
      return this.newPassword.length >= 6 &&
             this.confirmPassword === this.newPassword
    }
  },
  created () {
    // Get token from URL query parameter
    this.token = this.$route.query.token || ''
    if (!this.token) {
      this.tokenError = true
    }
  },
  methods: {
    submitResetPassword () {
      if (!this.isFormValid) return

      this.loading = true
      request.post(
        DB_ROUTES.RESET_PASSWORD,
        {
          Token: this.token,
          NewPassword: this.newPassword
        },
        () => {
          this.loading = false
          this.resetSuccess = true
          toast(this, 'success', 'Sucesso', 'Sua senha foi redefinida com sucesso!')
        },
        error => {
          this.loading = false
          console.error('Reset password error:', error)

          // Check if token is invalid or expired
          if (error.response && (error.response.status === 400 || error.response.status === 404)) {
            this.tokenError = true
          } else {
            toast(this, 'danger', 'Erro', 'Ocorreu um erro ao redefinir sua senha. Tente novamente.')
          }
        }
      )
    }
  }
}
</script>

<style lang="stylus" scoped>
@import '../style/colors.styl'
@import '../style/fonts.styl'
@import '../style/spacing.styl'

.logo-nav
  padding-left 85px

nav
  position relative
  z-index 2
  background-color secondary
  box-shadow shadow-md
  height 100px
  display flex
  align-items center

.interface
  min-height calc(100vh - 100px)
  display flex
  align-items center
  justify-content center
  background-color secondary
  position relative
  overflow hidden

.image
  width 100%
  height 100%
  object-fit cover
  position absolute
  top 0
  left 0
  z-index 1

.image-overlay
  position absolute
  top 0
  left 0
  width 100%
  height 100%
  background linear-gradient(135deg, rgba(255, 140, 66, 0.7) 0%, rgba(28, 78, 122, 0.8) 100%)
  z-index 2
  backdrop-filter blur(2px)

.reset-card
  position relative
  z-index 3
  width 480px
  max-width 90%
  background linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)
  border-radius radius-2xl
  box-shadow 0 20px 60px rgba(0, 0, 0, 0.3)
  border none
  padding 0
  animation fadeInUp 0.6s ease-out

@keyframes fadeInUp
  from
    opacity 0
    transform translateY(30px)
  to
    opacity 1
    transform translateY(0)

.reset-card-content
  padding space-2xl

.card-body
  padding 0

.reset-header
  text-align center
  margin-bottom space-xl

.header-icon
  width 80px
  height 80px
  margin 0 auto space-lg
  background linear-gradient(135deg, primary 0%, #FF7A29 100%)
  border-radius 50%
  display flex
  align-items center
  justify-content center
  box-shadow shadow-orange

  i
    font-size 2rem
    color white

.title
  font-size 1.75rem
  font-weight 700
  background linear-gradient(135deg, primary 0%, accent 100%)
  -webkit-background-clip text
  -webkit-text-fill-color transparent
  background-clip text
  margin-bottom space-sm

.error-title
  background linear-gradient(135deg, danger 0%, #d43f47 100%)
  -webkit-background-clip text
  -webkit-text-fill-color transparent
  background-clip text

.subtitle
  color #6c757d
  font-size 0.95rem
  margin 0
  line-height 1.5

.form-group
  margin-bottom space-lg

.label
  display flex
  align-items center
  font-weight 600
  color #2c3e50
  margin-bottom space-sm
  font-size 0.95rem

.label-icon
  margin-right space-xs
  color primary
  font-size 0.9rem

.modern-input
  height 50px
  border 2px solid #e0e0e0
  border-radius radius-lg
  padding 0 space-md
  padding-right 50px
  font-size 1rem
  transition all transition-base
  background-color white

  &:focus
    border-color primary
    box-shadow 0 0 0 4px rgba(255, 140, 66, 0.1)
    background-color white

  &::placeholder
    color #aaa

  &:disabled
    background-color #f5f5f5
    cursor not-allowed

  &.is-valid
    border-color success

  &.is-invalid
    border-color danger

.password-wrapper
  position relative

  .password-toggle
    position absolute
    right 15px
    top 50%
    transform translateY(-50%)
    background none
    border none
    color #6c757d
    cursor pointer
    padding space-sm
    transition all transition-fast
    z-index 10

    &:hover
      color primary

    i
      font-size 1.1rem

.password-hint
  display block
  color #6c757d
  font-size 0.8rem
  margin-top space-xs

.password-error
  display block
  color danger
  font-size 0.8rem
  margin-top space-xs

.btn-submit
  width 100%
  height 54px
  background linear-gradient(135deg, primary 0%, #FF7A29 100%)
  border none
  border-radius radius-lg
  font-weight 600
  font-size 1.05rem
  display flex
  align-items center
  justify-content center
  gap space-sm
  transition all transition-base
  box-shadow shadow-orange
  color white
  margin-bottom space-lg
  margin-top space-xl

  &:hover:not(:disabled)
    background linear-gradient(135deg, #FF7A29 0%, #FF6810 100%)
    transform translateY(-2px)
    box-shadow shadow-orange-lg
    color white

  &:active
    transform translateY(0)

  &:disabled
    opacity 0.7
    cursor not-allowed
    transform none

  i
    transition all transition-fast

  &:hover:not(:disabled) i
    transform translateX(4px)

.back-link
  text-align center
  margin-top space-md

  a
    color accent
    text-decoration none
    font-weight 500
    display inline-flex
    align-items center
    gap space-xs
    transition all transition-fast

    &:hover
      color primary
      text-decoration underline

    i
      font-size 0.85rem

// Success State Styles
.success-state
  text-align center

.success-icon
  width 100px
  height 100px
  margin 0 auto space-lg
  background linear-gradient(135deg, success 0%, #3D7A5C 100%)
  border-radius 50%
  display flex
  align-items center
  justify-content center
  animation scaleIn 0.5s ease-out

  i
    font-size 3rem
    color white

@keyframes scaleIn
  from
    transform scale(0)
    opacity 0
  to
    transform scale(1)
    opacity 1

// Error State Styles
.error-state
  text-align center

.error-icon
  width 100px
  height 100px
  margin 0 auto space-lg
  background linear-gradient(135deg, danger 0%, #d43f47 100%)
  border-radius 50%
  display flex
  align-items center
  justify-content center
  animation scaleIn 0.5s ease-out

  i
    font-size 3rem
    color white

.message
  color #495057
  font-size 1rem
  line-height 1.6
  margin-bottom space-xl

.btn-login
  display inline-flex
  align-items center
  justify-content center
  gap space-sm
  padding space-md space-xl
  background linear-gradient(135deg, primary 0%, #FF7A29 100%)
  color white
  text-decoration none
  border-radius radius-lg
  font-weight 600
  transition all transition-base
  box-shadow shadow-orange

  &:hover
    background linear-gradient(135deg, #FF7A29 0%, #FF6810 100%)
    transform translateY(-2px)
    color white
    text-decoration none
    box-shadow shadow-orange-lg

  i
    transition all transition-fast

  &:hover i
    transform translateX(4px)

.btn-request-new
  display inline-flex
  align-items center
  justify-content center
  gap space-sm
  padding space-md space-xl
  background linear-gradient(135deg, accent 0%, accent-hover 100%)
  color white
  text-decoration none
  border-radius radius-lg
  font-weight 600
  transition all transition-base
  margin-bottom space-lg

  &:hover
    background linear-gradient(135deg, accent-hover 0%, #3670A0 100%)
    transform translateY(-2px)
    color white
    text-decoration none

  i
    font-size 0.9rem

// Responsive
@media (max-width: 680px)
  .reset-card
    width 95%

  .reset-card-content
    padding space-xl space-lg

  .logo-nav
    padding-left 20px

  nav
    height 80px

  .title
    font-size 1.5rem

  .header-icon
    width 70px
    height 70px

    i
      font-size 1.75rem

  .success-icon,
  .error-icon
    width 80px
    height 80px

    i
      font-size 2.5rem
</style>
